import type { AgentMetadata } from '../types/agent.types';

/**
 * Complete Agent Registry for MVP-UX-002: Agent Discovery Hub
 * 35+ specialized AI agents across 5 categories
 */
export const agentRegistry: AgentMetadata[] = [
  // Intelligence Category (7 agents)
  {
    id: 'market-intelligence',
    name: 'Market Intelligence Agent',
    category: 'intelligence',
    status: 'active',
    description: 'Advanced market research and competitive analysis agent that provides deep insights into market trends, competitor strategies, and opportunities.',
    capabilities: ['Market Analysis', 'Competitive Intelligence', 'Trend Forecasting', 'Report Generation'],
    modelType: 'GPT-4',
    costPerQuery: 0.15
  },
  {
    id: 'persona-generator',
    name: 'Persona Generator',
    category: 'intelligence',
    status: 'active',
    description: 'Creates detailed buyer personas based on data analysis, behavioral patterns, and market research insights.',
    capabilities: ['Persona Creation', 'Behavioral Analysis', 'Demographics Research', 'Customer Insights'],
    modelType: 'GPT-4',
    costPerQuery: 0.12
  },
  {
    id: 'business-analyst',
    name: 'Business Analyst Agent',
    category: 'intelligence',
    status: 'active',
    description: 'Comprehensive business analysis including SWOT, market positioning, and strategic recommendations.',
    capabilities: ['SWOT Analysis', 'Market Positioning', 'Strategic Planning', 'Business Intelligence'],
    modelType: 'GPT-4',
    costPerQuery: 0.18
  },
  {
    id: 'research-scout',
    name: 'Research Scout',
    category: 'intelligence',
    status: 'active',
    description: 'Automated research agent that gathers, synthesizes, and validates information from multiple sources.',
    capabilities: ['Data Collection', 'Source Validation', 'Research Synthesis', 'Fact Checking'],
    modelType: 'Claude-3',
    costPerQuery: 0.10
  },
  {
    id: 'trend-analyst',
    name: 'Trend Analyst',
    category: 'intelligence',
    status: 'active',
    description: 'Identifies emerging trends, patterns, and future opportunities across industries and markets.',
    capabilities: ['Trend Identification', 'Pattern Recognition', 'Future Forecasting', 'Industry Analysis'],
    modelType: 'GPT-4',
    costPerQuery: 0.14
  },
  {
    id: 'data-analyst',
    name: 'Data Analyst Agent',
    category: 'intelligence',
    status: 'busy',
    description: 'Advanced data analysis and visualization agent for complex datasets and statistical insights.',
    capabilities: ['Statistical Analysis', 'Data Visualization', 'Predictive Modeling', 'Insights Generation'],
    modelType: 'Claude-3',
    costPerQuery: 0.16
  },
  {
    id: 'competitive-monitor',
    name: 'Competitive Monitor',
    category: 'intelligence',
    status: 'active',
    description: 'Continuous monitoring of competitor activities, pricing, and strategic moves across channels.',
    capabilities: ['Competitor Tracking', 'Price Monitoring', 'Strategy Analysis', 'Alert Systems'],
    modelType: 'GPT-4',
    costPerQuery: 0.11
  },

  // Content Category (9 agents)
  {
    id: 'content-strategist',
    name: 'Content Strategist',
    category: 'content',
    status: 'active',
    description: 'Develops comprehensive content strategies aligned with business goals and audience needs.',
    capabilities: ['Content Strategy', 'Editorial Planning', 'Audience Analysis', 'Content Calendar'],
    modelType: 'GPT-4',
    costPerQuery: 0.13
  },
  {
    id: 'copywriter-pro',
    name: 'Copywriter Pro',
    category: 'content',
    status: 'active',
    description: 'Professional copywriting for ads, emails, web pages, and marketing materials with conversion focus.',
    capabilities: ['Ad Copy', 'Email Marketing', 'Web Copy', 'Sales Pages', 'Conversion Optimization'],
    modelType: 'GPT-4',
    costPerQuery: 0.08
  },
  {
    id: 'blog-writer',
    name: 'Blog Writer Agent',
    category: 'content',
    status: 'active',
    description: 'Creates engaging, SEO-optimized blog content across various industries and topics.',
    capabilities: ['Blog Writing', 'SEO Optimization', 'Topic Research', 'Content Structure'],
    modelType: 'Claude-3',
    costPerQuery: 0.09
  },
  {
    id: 'social-media-manager',
    name: 'Social Media Manager',
    category: 'content',
    status: 'active',
    description: 'Manages social media content strategy, post creation, and engagement optimization.',
    capabilities: ['Social Strategy', 'Post Creation', 'Hashtag Research', 'Engagement Planning'],
    modelType: 'GPT-4',
    costPerQuery: 0.07
  },
  {
    id: 'video-script-writer',
    name: 'Video Script Writer',
    category: 'content',
    status: 'busy',
    description: 'Specialized in creating compelling video scripts for marketing, training, and entertainment.',
    capabilities: ['Script Writing', 'Storyboarding', 'Video Planning', 'Narrative Structure'],
    modelType: 'Claude-3',
    costPerQuery: 0.12
  },
  {
    id: 'email-marketer',
    name: 'Email Marketing Agent',
    category: 'content',
    status: 'active',
    description: 'Creates high-converting email campaigns, sequences, and automated marketing workflows.',
    capabilities: ['Email Campaigns', 'Automation Sequences', 'Subject Lines', 'A/B Testing'],
    modelType: 'GPT-4',
    costPerQuery: 0.10
  },
  {
    id: 'product-description-writer',
    name: 'Product Description Writer',
    category: 'content',
    status: 'active',
    description: 'Crafts compelling product descriptions that highlight benefits and drive conversions.',
    capabilities: ['Product Descriptions', 'Benefit Highlighting', 'Feature Explanation', 'Sales Copy'],
    modelType: 'GPT-4',
    costPerQuery: 0.08
  },
  {
    id: 'technical-writer',
    name: 'Technical Writer Agent',
    category: 'content',
    status: 'active',
    description: 'Creates clear, accurate technical documentation, guides, and instructional content.',
    capabilities: ['Technical Documentation', 'User Guides', 'API Documentation', 'Process Instructions'],
    modelType: 'Claude-3',
    costPerQuery: 0.11
  },
  {
    id: 'content-editor',
    name: 'Content Editor',
    category: 'content',
    status: 'active',
    description: 'Comprehensive content editing, proofreading, and quality enhancement services.',
    capabilities: ['Content Editing', 'Proofreading', 'Style Enhancement', 'Quality Assurance'],
    modelType: 'Claude-3',
    costPerQuery: 0.06
  },

  // Optimization Category (8 agents)
  {
    id: 'seo-optimizer',
    name: 'SEO Optimizer',
    category: 'optimization',
    status: 'active',
    description: 'Advanced SEO optimization for content, websites, and digital marketing campaigns.',
    capabilities: ['Keyword Research', 'On-Page SEO', 'Technical SEO', 'Content Optimization'],
    modelType: 'GPT-4',
    costPerQuery: 0.12
  },
  {
    id: 'conversion-optimizer',
    name: 'Conversion Optimizer',
    category: 'optimization',
    status: 'active',
    description: 'Analyzes and optimizes conversion paths, landing pages, and user experiences.',
    capabilities: ['Conversion Analysis', 'A/B Testing', 'Landing Page Optimization', 'UX Enhancement'],
    modelType: 'GPT-4',
    costPerQuery: 0.15
  },
  {
    id: 'performance-analyzer',
    name: 'Performance Analyzer',
    category: 'optimization',
    status: 'busy',
    description: 'Deep performance analysis of marketing campaigns, content, and business metrics.',
    capabilities: ['Performance Metrics', 'Campaign Analysis', 'ROI Tracking', 'Optimization Recommendations'],
    modelType: 'Claude-3',
    costPerQuery: 0.14
  },
  {
    id: 'price-optimizer',
    name: 'Price Optimizer',
    category: 'optimization',
    status: 'active',
    description: 'Dynamic pricing optimization based on market conditions, competition, and demand.',
    capabilities: ['Price Analysis', 'Competitive Pricing', 'Demand Forecasting', 'Profit Optimization'],
    modelType: 'GPT-4',
    costPerQuery: 0.16
  },
  {
    id: 'ad-optimizer',
    name: 'Ad Campaign Optimizer',
    category: 'optimization',
    status: 'active',
    description: 'Optimizes advertising campaigns across platforms for maximum ROI and performance.',
    capabilities: ['Ad Performance', 'Bid Optimization', 'Audience Targeting', 'Budget Allocation'],
    modelType: 'GPT-4',
    costPerQuery: 0.13
  },
  {
    id: 'workflow-optimizer',
    name: 'Workflow Optimizer',
    category: 'optimization',
    status: 'active',
    description: 'Analyzes and optimizes business workflows, processes, and operational efficiency.',
    capabilities: ['Process Analysis', 'Workflow Design', 'Efficiency Improvement', 'Automation Planning'],
    modelType: 'Claude-3',
    costPerQuery: 0.11
  },
  {
    id: 'customer-journey-optimizer',
    name: 'Customer Journey Optimizer',
    category: 'optimization',
    status: 'active',
    description: 'Maps and optimizes customer journeys for better engagement and conversion.',
    capabilities: ['Journey Mapping', 'Touchpoint Analysis', 'Experience Optimization', 'Conversion Funnels'],
    modelType: 'GPT-4',
    costPerQuery: 0.14
  },
  {
    id: 'resource-optimizer',
    name: 'Resource Optimizer',
    category: 'optimization',
    status: 'active',
    description: 'Optimizes resource allocation, scheduling, and utilization across projects and teams.',
    capabilities: ['Resource Planning', 'Capacity Management', 'Schedule Optimization', 'Team Allocation'],
    modelType: 'Claude-3',
    costPerQuery: 0.10
  },

  // Automation Category (7 agents)
  {
    id: 'campaign-automator',
    name: 'Campaign Automator',
    category: 'automation',
    status: 'active',
    description: 'Automates marketing campaign creation, execution, and optimization across channels.',
    capabilities: ['Campaign Automation', 'Multi-channel Management', 'Scheduling', 'Auto-optimization'],
    modelType: 'GPT-4',
    costPerQuery: 0.17
  },
  {
    id: 'lead-nurturing-agent',
    name: 'Lead Nurturing Agent',
    category: 'automation',
    status: 'active',
    description: 'Automated lead nurturing workflows with personalized communication sequences.',
    capabilities: ['Lead Scoring', 'Nurture Sequences', 'Personalization', 'Follow-up Automation'],
    modelType: 'GPT-4',
    costPerQuery: 0.12
  },
  {
    id: 'social-scheduler',
    name: 'Social Media Scheduler',
    category: 'automation',
    status: 'active',
    description: 'Automated social media posting, scheduling, and engagement across platforms.',
    capabilities: ['Post Scheduling', 'Cross-platform Publishing', 'Engagement Automation', 'Content Queuing'],
    modelType: 'Claude-3',
    costPerQuery: 0.08
  },
  {
    id: 'report-generator',
    name: 'Automated Report Generator',
    category: 'automation',
    status: 'busy',
    description: 'Generates comprehensive automated reports with data visualization and insights.',
    capabilities: ['Report Automation', 'Data Visualization', 'Scheduled Reports', 'Custom Dashboards'],
    modelType: 'GPT-4',
    costPerQuery: 0.13
  },
  {
    id: 'customer-support-bot',
    name: 'Customer Support Automation',
    category: 'automation',
    status: 'active',
    description: 'Intelligent customer support automation with natural language understanding.',
    capabilities: ['Automated Responses', 'Ticket Routing', 'FAQ Handling', 'Escalation Management'],
    modelType: 'Claude-3',
    costPerQuery: 0.09
  },
  {
    id: 'inventory-manager',
    name: 'Inventory Management Agent',
    category: 'automation',
    status: 'active',
    description: 'Automated inventory tracking, forecasting, and replenishment management.',
    capabilities: ['Inventory Tracking', 'Demand Forecasting', 'Auto-reordering', 'Stock Alerts'],
    modelType: 'GPT-4',
    costPerQuery: 0.11
  },
  {
    id: 'quality-assurance-bot',
    name: 'Quality Assurance Agent',
    category: 'automation',
    status: 'active',
    description: 'Automated quality control and assurance processes for content and products.',
    capabilities: ['Quality Checking', 'Compliance Monitoring', 'Error Detection', 'Standard Verification'],
    modelType: 'Claude-3',
    costPerQuery: 0.10
  },

  // Analytics Category (6 agents)
  {
    id: 'web-analytics-expert',
    name: 'Web Analytics Expert',
    category: 'analytics',
    status: 'active',
    description: 'Comprehensive web analytics analysis with actionable insights and recommendations.',
    capabilities: ['Traffic Analysis', 'User Behavior', 'Conversion Tracking', 'Performance Metrics'],
    modelType: 'GPT-4',
    costPerQuery: 0.14
  },
  {
    id: 'sales-analytics-agent',
    name: 'Sales Analytics Agent',
    category: 'analytics',
    status: 'active',
    description: 'Advanced sales performance analysis, forecasting, and pipeline optimization.',
    capabilities: ['Sales Metrics', 'Pipeline Analysis', 'Forecasting', 'Performance Tracking'],
    modelType: 'Claude-3',
    costPerQuery: 0.13
  },
  {
    id: 'customer-analytics-pro',
    name: 'Customer Analytics Pro',
    category: 'analytics',
    status: 'busy',
    description: 'Deep customer behavior analysis, segmentation, and lifetime value calculations.',
    capabilities: ['Customer Segmentation', 'Behavior Analysis', 'LTV Calculation', 'Churn Prediction'],
    modelType: 'GPT-4',
    costPerQuery: 0.16
  },
  {
    id: 'financial-analyst',
    name: 'Financial Analytics Agent',
    category: 'analytics',
    status: 'active',
    description: 'Financial performance analysis, budgeting, and ROI calculation across campaigns.',
    capabilities: ['ROI Analysis', 'Budget Tracking', 'Financial Forecasting', 'Cost Analysis'],
    modelType: 'Claude-3',
    costPerQuery: 0.15
  },
  {
    id: 'marketing-attribution-agent',
    name: 'Marketing Attribution Agent',
    category: 'analytics',
    status: 'active',
    description: 'Advanced marketing attribution modeling and cross-channel performance analysis.',
    capabilities: ['Attribution Modeling', 'Channel Analysis', 'Touch Point Tracking', 'Conversion Attribution'],
    modelType: 'GPT-4',
    costPerQuery: 0.17
  },
  {
    id: 'predictive-analyst',
    name: 'Predictive Analytics Agent',
    category: 'analytics',
    status: 'active',
    description: 'Machine learning-powered predictive analytics for forecasting and trend prediction.',
    capabilities: ['Predictive Modeling', 'Trend Forecasting', 'Risk Assessment', 'Scenario Planning'],
    modelType: 'GPT-4',
    costPerQuery: 0.19
  },

  // Tier 1 Content Agents - Epic 29 (18 specialized agents)
  {
    id: 'email-abm-executive',
    name: 'ABM Executive Email',
    category: 'content',
    status: 'active',
    description: 'High-stakes executive outreach for ABM campaigns with C-level messaging strategies.',
    capabilities: ['Executive Messaging', 'ABM Campaigns', 'C-Suite Outreach', 'Strategic Positioning'],
    modelType: 'GPT-4',
    costPerQuery: 0.15
  },
  {
    id: 'email-abm-champion',
    name: 'ABM Champion Email',
    category: 'content',
    status: 'active',
    description: 'Champion nurture sequences for building internal advocates in target accounts.',
    capabilities: ['Champion Nurturing', 'ABM Sequences', 'Internal Advocate Building', 'Relationship Development'],
    modelType: 'GPT-4',
    costPerQuery: 0.12
  },
  {
    id: 'email-abm-multistakeholder',
    name: 'ABM Multi-Stakeholder Email',
    category: 'content',
    status: 'active',
    description: 'Coordinated email campaigns targeting multiple stakeholders in buying committees.',
    capabilities: ['Multi-Threading', 'Buying Committee Outreach', 'Stakeholder Coordination', 'ABM Orchestration'],
    modelType: 'GPT-4',
    costPerQuery: 0.14
  },
  {
    id: 'email-smb-conversion',
    name: 'SMB Conversion Email',
    category: 'content',
    status: 'active',
    description: 'High-velocity conversion emails optimized for SMB sales cycles.',
    capabilities: ['SMB Outreach', 'Conversion Optimization', 'Quick-Close Sequences', 'Value Proposition'],
    modelType: 'GPT-4',
    costPerQuery: 0.08
  },
  {
    id: 'email-trigger-pricing',
    name: 'Pricing Trigger Email',
    category: 'content',
    status: 'active',
    description: 'Event-triggered emails for pricing discussions and quote follow-ups.',
    capabilities: ['Pricing Communication', 'Quote Follow-up', 'Trigger Campaigns', 'Deal Acceleration'],
    modelType: 'GPT-4',
    costPerQuery: 0.10
  },
  {
    id: 'landing-page-abm-microsite',
    name: 'ABM Microsite Generator',
    category: 'content',
    status: 'active',
    description: 'Personalized ABM microsites with account-specific messaging and content.',
    capabilities: ['Microsite Creation', 'ABM Personalization', 'Account-Specific Content', 'Landing Page Design'],
    modelType: 'GPT-4',
    costPerQuery: 0.18
  },
  {
    id: 'landing-page-abm-roi',
    name: 'ABM ROI Calculator Page',
    category: 'content',
    status: 'active',
    description: 'Interactive ROI calculator landing pages for ABM value demonstration.',
    capabilities: ['ROI Calculators', 'Value Quantification', 'Interactive Tools', 'Business Case Building'],
    modelType: 'GPT-4',
    costPerQuery: 0.16
  },
  {
    id: 'landing-page-smb-solution',
    name: 'SMB Solution Page',
    category: 'content',
    status: 'active',
    description: 'Conversion-optimized landing pages for SMB solution offerings.',
    capabilities: ['SMB Landing Pages', 'Solution Showcasing', 'Quick Conversion', 'Feature Highlighting'],
    modelType: 'GPT-4',
    costPerQuery: 0.12
  },
  {
    id: 'longform-abm-business-case',
    name: 'ABM Business Case Document',
    category: 'content',
    status: 'active',
    description: 'Comprehensive business case documents for enterprise ABM deals.',
    capabilities: ['Business Case Writing', 'ROI Documentation', 'Executive Summaries', 'Investment Justification'],
    modelType: 'GPT-4',
    costPerQuery: 0.20
  },
  {
    id: 'longform-abm-whitepaper',
    name: 'ABM Whitepaper Generator',
    category: 'content',
    status: 'active',
    description: 'Industry-specific whitepapers for thought leadership in ABM campaigns.',
    capabilities: ['Whitepaper Creation', 'Thought Leadership', 'Industry Analysis', 'Research Documentation'],
    modelType: 'GPT-4',
    costPerQuery: 0.22
  },
  {
    id: 'longform-buyers-guide',
    name: 'Buyer\'s Guide Creator',
    category: 'content',
    status: 'active',
    description: 'Comprehensive buyer guides for complex purchase decisions.',
    capabilities: ['Buyer Guides', 'Purchase Decision Support', 'Comparison Content', 'Evaluation Frameworks'],
    modelType: 'GPT-4',
    costPerQuery: 0.18
  },
  {
    id: 'case-study-roi',
    name: 'ROI Case Study',
    category: 'content',
    status: 'active',
    description: 'ROI-focused customer success stories with quantified outcomes.',
    capabilities: ['ROI Stories', 'Customer Success', 'Quantified Results', 'Impact Documentation'],
    modelType: 'GPT-4',
    costPerQuery: 0.15
  },
  {
    id: 'case-study-stakeholder',
    name: 'Stakeholder Case Study',
    category: 'content',
    status: 'active',
    description: 'Multi-stakeholder customer stories showing cross-functional impact.',
    capabilities: ['Stakeholder Stories', 'Cross-Functional Impact', 'Team Success', 'Organizational Change'],
    modelType: 'GPT-4',
    costPerQuery: 0.14
  },
  {
    id: 'sales-enable-battle-card',
    name: 'Battle Card Generator',
    category: 'content',
    status: 'active',
    description: 'Competitive battle cards for sales team enablement.',
    capabilities: ['Battle Cards', 'Competitive Analysis', 'Sales Enablement', 'Objection Handling'],
    modelType: 'GPT-4',
    costPerQuery: 0.12
  },
  {
    id: 'sales-enable-one-pager',
    name: 'Stakeholder One-Pager',
    category: 'content',
    status: 'active',
    description: 'Role-specific one-pagers for different stakeholders in buying committees.',
    capabilities: ['One-Pagers', 'Role-Specific Content', 'Quick Reference', 'Value Summaries'],
    modelType: 'GPT-4',
    costPerQuery: 0.10
  },
  {
    id: 'sales-enable-pitch-deck',
    name: 'Pitch Deck Creator',
    category: 'content',
    status: 'active',
    description: 'Customizable pitch decks for different sales scenarios and audiences.',
    capabilities: ['Pitch Decks', 'Presentation Design', 'Story Flow', 'Visual Messaging'],
    modelType: 'GPT-4',
    costPerQuery: 0.16
  },
  {
    id: 'consensus-champion-pitch',
    name: 'Champion Pitch Guide',
    category: 'content',
    status: 'active',
    description: 'Internal champion enablement content for driving consensus.',
    capabilities: ['Champion Enablement', 'Internal Selling', 'Consensus Building', 'Stakeholder Alignment'],
    modelType: 'GPT-4',
    costPerQuery: 0.14
  },
  {
    id: 'partner-messaging-choreography',
    name: 'Partner Messaging Orchestrator',
    category: 'content',
    status: 'active',
    description: 'Coordinated partner messaging and co-marketing content generation.',
    capabilities: ['Partner Content', 'Co-Marketing', 'Alliance Messaging', 'Joint Value Propositions'],
    modelType: 'GPT-4',
    costPerQuery: 0.15
  }
];

// Helper functions for agent discovery
export const searchAgents = (query: string): AgentMetadata[] => {
  if (!query.trim()) return agentRegistry;
  
  const lowercaseQuery = query.toLowerCase();
  return agentRegistry.filter(agent => 
    agent.name.toLowerCase().includes(lowercaseQuery) ||
    agent.description.toLowerCase().includes(lowercaseQuery) ||
    agent.capabilities.some(cap => cap.toLowerCase().includes(lowercaseQuery))
  );
};

export const filterAgentsByCategory = (category: string): AgentMetadata[] => {
  if (category === 'all') return agentRegistry;
  return agentRegistry.filter(agent => agent.category === category);
};

export const getAgentById = (id: string): AgentMetadata | undefined => {
  return agentRegistry.find(agent => agent.id === id);
};